/**
 * Core logout service
 *
 * Centralizes all logout operations: Amplify signOut, storage cleanup,
 * user context clearing (Sentry, Intercom, app state), SSE disconnect,
 * and pending request cancellation.
 *
 * Used by:
 * - useAuth.logout() — main user-initiated logout flow
 * - useSSE.forceSignOut() — emergency logout during auth failures
 * - api.ts 401 handler — logout on token refresh failure
 */

import type { App } from 'vue';
import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded
} from 'vue-router';

import { signOut } from 'aws-amplify/auth';

import { setUserProfile } from '@/composables/useUserProfile';
import { setUserSettings } from '@/composables/useUserSettings';
import { authStorage } from '@/config/amplifyConfig';
import { clearSentryUser, logAppError } from '@/config/sentryConfig';
import { saveIntendedRoute } from '@/router/intendedRoute';
import { cancelPendingRequests } from '@/services/api';
import { sseRegistry } from '@/services/auth/sseRegistry';
import useAuthStore from '@/stores/auth';
import useScopesStore from '@/stores/scopes';
import { clearIntercomUser } from '@/utils/intercomUtils';
import { resetAllStores } from '@/utils/resetStores';

/**
 * Enum defining reasons for logout operations
 */
export enum LogoutReason {
  SessionExpired = 'session_expired',
  InitializationError = 'initialization_error'
}

/**
 * Type for LogoutReason enum values
 */
export type LogoutReasonValue = LogoutReason;

/**
 * Helper to safely execute a cleanup step and collect errors.
 * Allows all steps to run even if individual ones fail.
 */
const executeStep = async (
  step: string,
  fn: () => Promise<void> | void,
  errors: Array<{ step: string; error: unknown }>
) => {
  try {
    await Promise.resolve(fn());
  } catch (error) {
    errors.push({ step, error });
  }
};

/**
 * Core logout operations: sign out, clear storage/context, disconnect SSE
 *
 * Best-effort cleanup that always completes. Individual step failures are
 * logged at warning level but do not prevent the logout flow from completing.
 * This ensures the app never gets stuck in a partially signed-out state.
 *
 * @param app - Optional Vue app instance (retained for API compatibility)
 * @param reason - Optional reason for the logout (for logging/tracking)
 * @param intendedRoute - Optional route to return to after re-login. Saved here
 *   (rather than by the caller) because cleanup clears sessionStorage, so the
 *   save must happen afterwards — centralising it removes that ordering footgun.
 */
export const performLogout = async (
  app?: App,
  reason?: LogoutReasonValue,
  intendedRoute?: RouteLocationNormalized | RouteLocationNormalizedLoaded
) => {
  const errors: Array<{ step: string; error: unknown }> = [];

  // --- Signal logout to API layer ---
  // Prevents new requests and aborts in-flight API calls
  await executeStep(
    'cancelPendingRequests',
    () => cancelPendingRequests(),
    errors
  );

  // --- Disconnect all streams ---
  // Stop all SSE connections to prevent reconnection attempts
  await executeStep(
    'sseRegistry.disconnectAll',
    () => sseRegistry.disconnectAll(),
    errors
  );

  // --- Sign out from Amplify ---
  // Invalidate the authentication session
  await executeStep('signOut', () => signOut(), errors);

  // --- Clear browser storage ---
  // Remove auth tokens and session data
  await executeStep(
    'authStorage.clear',
    async () => {
      await authStorage.clear();
      sessionStorage.clear();
    },
    errors
  );

  // --- Remember where to return after re-login ---
  // Must run after the sessionStorage clear above, otherwise it's wiped.
  if (intendedRoute) {
    await executeStep(
      'saveIntendedRoute',
      () => saveIntendedRoute(intendedRoute),
      errors
    );
  }

  // --- Clear user context (observability) ---
  // Stop tracking the user in monitoring/analytics
  await executeStep('clearSentryUser', () => clearSentryUser(), errors);
  await executeStep('clearIntercomUser', () => clearIntercomUser(), errors);

  // --- Reset application state ---
  // Clear all Pinia stores and the module-level user profile/settings state.
  await executeStep(
    'resetAllStores',
    () => {
      resetAllStores();
      useScopesStore().resetTimezone();
      setUserProfile(null);
      setUserSettings(null);
    },
    errors
  );

  // --- Report any cleanup failures ---
  // Log but don't throw: logout must always complete to avoid leaving the app
  // in a partially signed-out state. Callers expect navigation to /login to succeed.
  if (errors.length > 0) {
    errors.forEach(({ step, error }) => {
      logAppError({
        error,
        functionName: 'performLogout',
        metadata: { step },
        level: 'warning'
      });
    });
  }
};

/**
 * Complete logout flow: cleanup + navigation to login
 *
 * Combines core logout operations with navigation. Always completes the
 * sign-out flow regardless of individual cleanup failures—reported errors
 * don't prevent navigation to /login.
 *
 * @param app - Optional Vue app instance (retained for API compatibility)
 * @param reason - Optional reason for the logout (for logging/tracking)
 * @param intendedRoute - Optional route to return to after re-login
 */
export const logout = async (
  app?: App,
  reason?: LogoutReasonValue,
  intendedRoute?: RouteLocationNormalized | RouteLocationNormalizedLoaded
) => {
  // Capture auth state before performLogout() clears it (resetAllStores()).
  // Only a logout that tears down a real session needs the reload below.
  const wasAuthenticated = useAuthStore().isUserAuthenticated;

  await performLogout(app, reason, intendedRoute);
  // Hard-navigate to a clean base-rooted URL. router.push() in hash-history
  // mode only updates the hash, leaving any corrupt server path intact (e.g.
  // /depot/gls-de/30/#/login). Building from origin + BASE_URL guarantees we
  // always land on <base>/#/login regardless of where the user was, without
  // dropping the base path on preview builds (/previews/pr-N/), which would
  // navigate into the root deployment's app instead. See docs/gotchas.md.
  const queryString = reason ? `?reason=${reason}` : '';
  const target =
    window.location.origin + import.meta.env.BASE_URL + '#/login' + queryString;
  window.location.assign(target);
  // Reload to force a fresh document (clearing module-scoped state, incl. the
  // shut-down Intercom session) only when we tore down a real session AND
  // assign() is a same-document hash change. Skip it when already unauthenticated
  // (a boot-time failure on the login page would reboot into the same failure and
  // loop) or when the pathname differs (assign() already navigates, and a
  // same-tick reload() would race it). See docs/gotchas.md "Routing".
  if (
    wasAuthenticated &&
    new URL(target).pathname === window.location.pathname
  ) {
    window.location.reload();
  }
};
